import { useLayoutEffect, useRef, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { ChipActivo } from './chip-activo';
import { ATTR_CHIP_FILTRO, type ChipFiltro, type MetaFiltrosProps } from './tipos';

// Meta-row de una página con filtros (pieza compartida, Ajuste #16 y #28;
// receta obras-filters.tsx; relevamiento de la página §3 y §4; síntesis de la
// home §2 «Meta-row y chips»): el conteo (la ÚNICA región viva de la fila),
// los chips de los filtros activos en una lista, «Limpiar todo» con 2 o más
// (Button ghost sm) y el slot derecho. La REGLA DE FOCO al quitar chips vive
// acá: solo esta fila sabe que el chip se quitó desde ella (si los chips
// cambian por el popover o por el Enter del buscador, el foco no se toca).

/** Foco pendiente de mover después del próximo cambio de chips. */
type FocoPendiente<K extends string> =
  /** Se quitó el chip `key`, que estaba en la posición `indice`. */
  | { tipo: 'chip'; key: K; indice: number; antes: readonly ChipFiltro<K>[] }
  /** «Limpiar todo». */
  | { tipo: 'limpiar'; antes: readonly ChipFiltro<K>[] };

/** Extensión ADITIVA de `MetaFiltrosProps` (decisión b del orquestador,
 *  fixer «vista», R4a): un slot opcional, hermano inmediato de la región
 *  viva del conteo pero AFUERA de ella. Se declara acá (no en
 *  `./tipos.ts`, fuera de este archivo en esta ola) para no tocar el
 *  contrato compartido — es 100% compatible con `MetaFiltrosProps<K>`, así
 *  que el Avivador (que no pasa `trasConteo`) sigue tipando igual. Ver el
 *  comentario del JSX más abajo para el motivo (hallazgo #2
 *  home-comportamiento: la píldora «Modo archivo · …» de la home no puede
 *  vivir DENTRO de `aria-live`, se reanunciaría en cada tecleo). */
type MetaFiltrosPropsConTrasConteo<K extends string> = MetaFiltrosProps<K> & {
  /** Pegado, hermano inmediato del `<span aria-live>` del conteo — misma
   *  línea, pero FUERA de la región viva (home: la píldora «Modo archivo
   *  · …»; el Avivador no lo pasa). */
  trasConteo?: ReactNode;
};

export function MetaFiltros<K extends string>({
  conteo,
  trasConteo,
  chips,
  onQuitarChip,
  onLimpiarTodo,
  focoSinChipsRef,
  derecha,
  etiquetaChips = 'Filtros activos',
}: MetaFiltrosPropsConTrasConteo<K>) {
  const listaRef = useRef<HTMLUListElement>(null);
  const pendiente = useRef<FocoPendiente<K> | null>(null);

  // Después del render en que cambian los chips (antes del paint, sin parpadeo
  // del anillo en <body>). Se resuelve en el PRIMER cambio de `chips` después
  // del clic: si la página no quitó el chip, no se mueve nada y se descarta.
  useLayoutEffect(() => {
    const p = pendiente.current;
    if (!p || chips === p.antes) return;
    pendiente.current = null;

    const sinChips = (): void => {
      focoSinChipsRef.current?.focus();
    };
    if (p.tipo === 'limpiar') {
      sinChips();
      return;
    }
    if (chips.some(c => c.key === p.key)) return;

    // Al chip que quedó en esa posición; si era el último, al anterior; si no
    // queda ninguno, al destino sin chips (el buscador).
    const botones = listaRef.current?.querySelectorAll<HTMLElement>(`[${ATTR_CHIP_FILTRO}]`);
    const destino = botones ? (botones[p.indice] ?? botones[p.indice - 1] ?? null) : null;
    if (destino) destino.focus();
    else sinChips();
  }, [chips, focoSinChipsRef]);

  function quitar(key: K, indice: number): void {
    pendiente.current = { tipo: 'chip', key, indice, antes: chips };
    onQuitarChip(key);
  }

  function limpiarTodo(): void {
    pendiente.current = { tipo: 'limpiar', antes: chips };
    onLimpiarTodo();
  }

  return (
    <div className="mb-4 flex min-h-[26px] flex-wrap items-center gap-2 text-[13px] text-muted-foreground [@media(hover:none)]:min-h-11">
      {/* aria-atomic: si React cambia solo el texto de un <b>, el lector
          anuncia la frase entera y no «4» suelto. `trasConteo` va PEGADO,
          hermano inmediato de esta región viva, pero AFUERA de ella (Ajuste
          de la home, hallazgo #2 home-comportamiento): la home lo usa para
          la píldora «Modo archivo · …», que no tiene que reanunciarse en
          cada tecleo del buscador solo porque el conteo cambió. */}
      <span aria-live="polite" aria-atomic="true" className="[&_b]:font-semibold [&_b]:tabular-nums [&_b]:text-foreground">
        {conteo}
      </span>
      {trasConteo}

      {chips.length > 0 && (
        <ul ref={listaRef} aria-label={etiquetaChips} className="m-0 flex min-w-0 max-w-full list-none flex-wrap items-center gap-2 p-0">
          {chips.map((chip, i) => (
            <li key={chip.key} className="flex min-w-0 max-w-full">
              <ChipActivo chip={chip} onQuitar={k => quitar(k, i)} />
            </li>
          ))}
        </ul>
      )}
      {chips.length >= 2 && (
        <Button type="button" variant="ghost" size="sm" onClick={limpiarTodo}>
          Limpiar todo
        </Button>
      )}

      {derecha != null && <div className="ml-auto flex items-center gap-1">{derecha}</div>}
    </div>
  );
}
